/**
 * AdminGuard - Protects /admin/* routes with backend session check.
 *
 * Flow: AuthGuard (isAuthenticated?) → AdminGuard (isAdmin?)
 * Non-admins are redirected to /unauthorized.
 */

import type { FC, ReactNode } from 'react';
import { Navigate } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  RefreshIcon,
  Typography,
} from '@/shared/ui/mui';
import { useAdminSession } from '../hooks/useAdminSession';

interface AdminGuardProps {
  children: ReactNode;
}

export const AdminGuard: FC<AdminGuardProps> = ({ children }) => {
  const {
    isLoading,
    isAdmin,
    error,
    isForbidden,
    refresh,
  } = useAdminSession();

  if (isLoading) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          height: '100vh',
          gap: 2,
        }}
      >
        <CircularProgress />
        <Typography variant="body2" color="text.secondary">
          Verifying admin access...
        </Typography>
      </Box>
    );
  }

  if (isForbidden) {
    return <Navigate to="/unauthorized" replace />;
  }

  if (error || !isAdmin) {
    return (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '60vh',
          px: 2,
        }}
      >
        <Alert
          severity="warning"
          action={
            <Button
              color="inherit"
              size="small"
              startIcon={<RefreshIcon />}
              onClick={() => void refresh()}
            >
              Retry
            </Button>
          }
          sx={{ maxWidth: 560 }}
        >
          <Typography variant="subtitle2" fontWeight={700}>
            Could not verify admin access
          </Typography>
          <Typography variant="body2">
            Your sign-in is active, but the admin session check did not
            complete. Retry once the token finishes refreshing.
          </Typography>
        </Alert>
      </Box>
    );
  }

  return children;
};
